import { useState, useEffect } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Plus, Pencil, Trash2, Quote, Upload } from 'lucide-react';
import Loading from '@/components/loading';
import { useAdminPreview } from '@/lib/admin-preview-context';

function parseCsv(text: string): { quote: string; author: string; image_url: string }[] {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];

  // Detect header row
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes('quote') || firstLine.includes('author') || firstLine.includes('image');
  const dataLines = hasHeader ? lines.slice(1) : lines;

  return dataLines.map((line) => {
    // Simple CSV parse handling quoted fields
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());

    return {
      quote: fields[0] || '',
      author: fields[1] || '',
      image_url: fields[2] || '',
    };
  }).filter((row) => row.quote.length > 0);
}

interface QuoteRow {
  id: string;
  quote: string;
  author: string;
  image_url: string | null;
  active: boolean;
  created_at: string;
}

export default function AdminQuotes() {
  const { toast } = useToast();
  const { isPreviewMode } = useAdminPreview();
  const roleScope = isPreviewMode ? 'hoofdhartuser' : null;
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<QuoteRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    quote: '',
    author: '',
    image_url: '',
    active: false,
  });

  const fetchQuotes = async () => {
    setLoading(true);
    let query = supabase
      .from('quotes')
      .select('*')
      .order('created_at', { ascending: false });

    if (roleScope) {
      query = query.eq('role_scope', roleScope);
    } else {
      query = query.is('role_scope', null);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching quotes:', error);
      toast({ title: 'Fout', description: 'Kon quotes niet laden.', variant: 'destructive' });
    } else {
      setQuotes(data ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchQuotes();
  }, [isPreviewMode]);

  const openCreateDialog = () => {
    setEditingQuote(null);
    setFormData({ quote: '', author: '', image_url: '', active: false });
    setDialogOpen(true);
  };

  const openEditDialog = (q: QuoteRow) => {
    setEditingQuote(q);
    setFormData({
      quote: q.quote,
      author: q.author,
      image_url: q.image_url ?? '',
      active: q.active,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // If setting this quote as active, deactivate all others first
      if (formData.active) {
        await supabase.from('quotes').update({ active: false }).eq('active', true);
      }

      if (editingQuote) {
        const { error } = await supabase
          .from('quotes')
          .update({
            quote: formData.quote,
            author: formData.author,
            image_url: formData.image_url || null,
            active: formData.active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingQuote.id);

        if (error) throw error;
        toast({ title: 'Quote bijgewerkt' });
      } else {
        const { error } = await supabase.from('quotes').insert({
          quote: formData.quote,
          author: formData.author,
          image_url: formData.image_url || null,
          active: formData.active,
          role_scope: roleScope,
        });

        if (error) throw error;
        toast({ title: 'Quote toegevoegd' });
      }

      setDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      console.error('Error saving quote:', error);
      toast({ title: 'Fout', description: 'Kon quote niet opslaan.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Weet je zeker dat je deze quote wilt verwijderen?')) return;

    const { error } = await supabase.from('quotes').delete().eq('id', id);
    if (error) {
      toast({ title: 'Fout', description: 'Kon quote niet verwijderen.', variant: 'destructive' });
    } else {
      toast({ title: 'Quote verwijderd' });
      fetchQuotes();
    }
  };

  const handleToggleActive = async (q: QuoteRow) => {
    try {
      if (!q.active) {
        // Deactivate all others first
        await supabase.from('quotes').update({ active: false }).eq('active', true);
      }
      await supabase.from('quotes').update({ active: !q.active }).eq('id', q.id);
      fetchQuotes();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);

      if (rows.length === 0) {
        toast({ title: 'Leeg bestand', description: 'Geen quotes gevonden in het CSV bestand.', variant: 'destructive' });
        return;
      }

      const insertData = rows.map((row) => ({
        quote: row.quote,
        author: row.author,
        image_url: row.image_url || null,
        active: false,
        role_scope: roleScope,
      }));

      const { error } = await supabase.from('quotes').insert(insertData);
      if (error) throw error;

      toast({ title: `${rows.length} quotes geïmporteerd` });
      fetchQuotes();
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast({ title: 'Fout', description: 'Kon CSV niet importeren.', variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Quote className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Quotes{isPreviewMode ? ' (HoofdnaarHart)' : ''}</h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => document.getElementById('csv-upload')?.click()}
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Importeren...' : 'CSV Importeren'}
          </Button>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleCsvUpload}
          />
          <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe Quote
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Beheer de quotes die op de homepage worden weergegeven. Markeer een quote als actief om deze op de homepage te tonen.
        Je kunt ook meerdere quotes tegelijk importeren via een CSV bestand met kolommen: quote, author, image_url.
      </p>

      {quotes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed rounded-lg">
          Nog geen quotes. Voeg je eerste quote toe!
        </div>
      ) : (
        <div className="grid gap-4">
          {quotes.map((q) => (
            <Card key={q.id} className={`p-4 ${q.active ? 'ring-2 ring-green-500' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {q.active && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        Actief
                      </span>
                    )}
                  </div>
                  <p className="text-lg italic">"{q.quote}"</p>
                  <p className="text-sm text-muted-foreground mt-1">- {q.author}</p>
                  {q.image_url && (
                    <img
                      src={q.image_url}
                      alt="Quote achtergrond"
                      className="mt-2 h-20 w-32 object-cover rounded"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Actief</span>
                    <Switch
                      checked={q.active}
                      onCheckedChange={() => handleToggleActive(q)}
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(q)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingQuote ? 'Quote Bewerken' : 'Nieuwe Quote'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quote">Quote Tekst</Label>
              <Textarea
                id="quote"
                value={formData.quote}
                onChange={(e) => setFormData((prev) => ({ ...prev, quote: e.target.value }))}
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="author">Auteur</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData((prev) => ({ ...prev, author: e.target.value }))}
                required
              />
            </div>
            <ContentImageField
              id="image_url"
              value={formData.image_url}
              onChange={(url) =>
                setFormData((prev) => ({ ...prev, image_url: url }))
              }
              label="Afbeelding (optioneel)"
            />
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, active: checked }))}
              />
              <Label>Actief (toon op homepage)</Label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annuleren
              </Button>
              <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? 'Opslaan...' : editingQuote ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
